import Card from "../Card"
import { useAppDispatch, useAppSelector } from "../../redux/store/hooks"
import {
    selectDashboardSelectedPatient,
    selectReports
} from "../../redux/ProfessionalDashboard"
import notNull from "../../util/notNull"
import SmallHeadingComponent from "../SmallHeadingComponent"
import {
    DownloadReportSpecification,
    FetchReportsResponsePayload,
    FetchReportsSpecification,
    GenerateReportRequestPayload,
    GenerateReportSpecification,
    GetPatientsOfProfessionalResponsePayload
} from "pokal_api"
import Button from "../Button"
import { useEffect, useState } from "react"
import { dispatchThunk } from "../../redux/thunk/thunkify"
import { showNotification } from "../../redux/Notification"
import { ArrayElement } from "../../types/ArrayElement"
import mockPatient from "../../onboarding/mock/mockPatient"
import {
    hidePatientOverviewReport,
    mockPatientUsed,
    selectOnboardingIsRunningState,
    selectOnboardingStep
} from "../../redux/Onboarding"

function ReportList() {
    const [selectedPatient, setSelectedPatient] =
        useState<ArrayElement<GetPatientsOfProfessionalResponsePayload> | null>(
            null
        )

    const fetchedPatient = useAppSelector(selectDashboardSelectedPatient)
    const mockedPatient = mockPatient()

    const running = useAppSelector(selectOnboardingIsRunningState)
    const step = useAppSelector(selectOnboardingStep)

    const dispatch = useAppDispatch()

    const [reports, setReports] = useState<FetchReportsResponsePayload | null>(
        null
    )

    const [showReadReports, setShowReadReports] = useState<boolean>(false)

    const fetchedReports = useAppSelector(selectReports)
    const mockedReports: FetchReportsResponsePayload = []

    useEffect(() => {
        if (mockPatientUsed(running, step)) {
            setSelectedPatient(mockedPatient[0])
            setReports(mockedReports)
        } else {
            setSelectedPatient(fetchedPatient)
            // Filter out test reports (reports with TEST in their name)
            const filteredReports = fetchedReports?.filter(report => !report.path.includes('TEST')) || null
            setReports(filteredReports)
        }
    }, [running, step, fetchedReports])

    const [loading, setLoading] = useState<boolean>(false)
    function onGenerateReport() {
        if (notNull(selectedPatient)) {
            setLoading(true)
            const reportRequest: GenerateReportRequestPayload & {
                frontendOrigin: string
            } = {
                patient: selectedPatient.id,
                frontendOrigin: window.location.origin
            }

            GenerateReportSpecification.request(reportRequest)
                .then(() => {
                    dispatchThunk(dispatch, FetchReportsSpecification, {
                        patient: selectedPatient.id
                    })
                    setLoading(false)
                })
                .catch(() => {
                    setLoading(false)
                })
        }
    }

    function onViewReport(report: string) {
        DownloadReportSpecification.request({ path: report })
            .then((file) => {
                const url = window.URL.createObjectURL(file)
                const tempLink = document.createElement("a")
                tempLink.href = url
                tempLink.setAttribute("download", `test.pdf`)

                document.body.appendChild(tempLink)
                tempLink.click()

                document.body.removeChild(tempLink)
                window.URL.revokeObjectURL(url)

                if (notNull(selectedPatient)) {
                    dispatchThunk(dispatch, FetchReportsSpecification, {
                        patient: selectedPatient.id
                    })
                }
            })
    }

    const unreadReports = reports?.filter(report => !report.read) || []
    const readReports = reports?.filter(report => report.read) || []

    return (
        <Card blur={hidePatientOverviewReport(running, step)}>
            <div className={"flex flex-col space-y-4"}>
                <SmallHeadingComponent>Bericht-Auswahl</SmallHeadingComponent>
                <div>
                    <p>
                        Sie können sich hier die Berichte zu den Daten Ihrer Patient:innen in Echtzeit erstellen lassen. 
                        Im generierten Bericht finden Sie einen Link, über den Sie bestätigen können, dass Sie den Bericht gelesen haben.
                    </p>
                    
                </div>
                {notNull(selectedPatient) &&
                    notNull(reports) &&
                    reports.length > 0 && (
                        <div>
                            {/* Unread Reports */}
                            {unreadReports.map((report) => (
                                <div
                                    key={report.path}
                                    className={"flex space-x-2"}>
                                    <div className={"w-56"}>
                                        Bericht vom{" "}
                                        {new Date(
                                            report.date
                                        ).toLocaleDateString("de")}
                                    </div>
                                    <div
                                        onClick={() =>
                                            onViewReport(report.path)
                                        }
                                        className={
                                            "text-primary-500 hover:underline cursor-pointer w-56"
                                        }>
                                        Download
                                    </div>
                                    {!report.viewed && (
                                        <div
                                            className={
                                                "font-bold text-rose-600"
                                            }>
                                            Wichtig: Sie haben diesen Bericht
                                            noch nicht abgerufen.
                                        </div>
                                    )}
                                    {report.viewed && !report.read && (
                                        <div
                                            className={
                                                "font-bold text-rose-600"
                                            }>
                                            Wichtig: Sie haben diesen Bericht
                                            noch nicht gelesen.
                                        </div>
                                    )}
                                </div>
                            ))}
                            
                            {/* Read Reports Section */}
                            {readReports.length > 0 && (
                                <div className="mt-4">
                                    <button
                                        onClick={() => setShowReadReports(!showReadReports)}
                                        className="text-primary-500 hover:underline cursor-pointer mb-2"
                                    >
                                        {showReadReports ? '▼' : '▶'} Gelesene Berichte ({readReports.length})
                                    </button>
                                    
                                    {showReadReports && (
                                        <div className="ml-4 space-y-2">
                                            {readReports.map((report) => (
                                                <div
                                                    key={report.path}
                                                    className={"flex space-x-2 text-gray-500"}>
                                                    <div className={"w-56"}>
                                                        Bericht vom{" "}
                                                        {new Date(
                                                            report.date
                                                        ).toLocaleDateString("de")}
                                                    </div>
                                                    <div
                                                        onClick={() =>
                                                            onViewReport(report.path)
                                                        }
                                                        className={
                                                            "text-primary-500 hover:underline cursor-pointer w-56"
                                                        }>
                                                        Download
                                                    </div>
                                                    <div className="text-green-600">
                                                        ✓ Gelesen
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                {selectedPatient === null && (
                    <div className={"italic"}>
                        Aktuell kein:e Patient:in ausgewählt. Um die Berichte
                        einer bestimmten Patient:in sehen zu können, wählen Sie
                        bitte eine Patient:in in der Patient:innen-Auswahl aus.
                    </div>
                )}
                {notNull(selectedPatient) &&
                    (reports === null || reports.length === 0) && (
                        <div className={"italic"}>
                            Für den / die ausgewählte Patient:in sind keine
                            Berichte verfügbar.
                        </div>
                    )}
                <div className={"pt-10"}>
                    <Button disabled={loading} onClick={onGenerateReport}>
                        Neuen Bericht erstellen
                    </Button>
                </div>
            </div>
        </Card>
    )
}

export default ReportList
